/**
 * Scheduled publish of an approved weekly report, as the cockpit shows it
 * (SPEC-CIS-REPORT-SCHEDULED-PUBLISH-001 section 6). Pure functions over the
 * server's own row fields (`scheduleState`, `publishAtUtc`, `publishTimeZone`,
 * `scheduleError`, `publishedVia`, `scheduleDefault`), which the API reads from
 * the server-side schedule record. When the server offers no schedule (switch
 * off, zone not set) and the row is not scheduled, nothing here is shown and
 * the screen is exactly as before.
 */

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Mon 02 Nov 09:00" in the site's zone ("" when unparseable). */
export function formatSiteTime(iso, timeZone) {
  const t = Date.parse(iso || "");
  if (!Number.isFinite(t)) return "";
  let parts;
  try {
    parts = Object.fromEntries(
      new Intl.DateTimeFormat("en-GB", { timeZone: timeZone || "UTC", hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })
        .formatToParts(new Date(t))
        .map((p) => [p.type, p.value])
    );
  } catch {
    return "";
  }
  const day = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
  return `${WEEKDAY[day.getUTCDay()]} ${String(parts.day).padStart(2, "0")} ${MONTH[Number(parts.month) - 1]} ${parts.hour}:${parts.minute}`;
}

/** The stop reason in words (section 6). */
export const STOP_REASON_TEXT = {
  changed_since_approval: "changed since approval — reopen and re-approve",
  schedule_missed: "missed",
  scheduler_not_authorized: "scheduler no longer site manager",
  period_overlap: "another published report covers this period",
  report_has_unresolved_markers: "the Word file has review markers — reopen and resolve them",
  report_regeneration_required: "reopen and regenerate this report",
  publish_target_missing: "the project site's Weekly Reports folder is missing",
  publish_target_not_admitted: "the project site is not set up for publishing",
  published_number_held: "another approved report must be published or reopened first",
};

export function stopReasonText(code) {
  return STOP_REASON_TEXT[code] || (code ? `not published (${code})` : "not published");
}

/** The dialog's Schedule option label: "Schedule — Mon 02 Nov 09:00 site time". */
export function scheduleOptionLabel(scheduleDefault) {
  if (!scheduleDefault || !scheduleDefault.publishAtUtc) return "";
  return `Schedule — ${formatSiteTime(scheduleDefault.publishAtUtc, scheduleDefault.timeZone)} site time`;
}

/**
 * Section 6: after a successful approve, which second step the Approve
 * dialog's Confirm runs (null: none, the existing PDF-ready card) and with
 * which etag (the approve answer's own row etag).
 */
export function afterApproveStep(d, approveResult, { canSchedule, canPublish } = {}) {
  if (!d || !d.scheduleDefault) return null;
  const next = d.next || "schedule";
  const etag = (approveResult && approveResult.etag) || d.etag;
  if (next === "schedule" && canSchedule) return { action: "schedule", etag };
  if (next === "publish" && canPublish) return { action: "publish", etag };
  return null;
}

/**
 * The schedule view of one report row, or null when nothing changes on screen.
 *   state: "scheduled" | "stopped" | "approved" | "issued"
 */
export function shapeSchedule(report) {
  if (!report) return null;
  if (report.reportStatus === "Issued") {
    return report.publishedVia === "schedule" ? { state: "issued", publishedAutomatically: true, note: "Published automatically" } : null;
  }
  if (report.reportStatus !== "Approved") return null;
  const def = report.scheduleDefault && report.scheduleDefault.publishAtUtc ? report.scheduleDefault : null;
  if (report.scheduleState === "Scheduled") {
    const when = formatSiteTime(report.publishAtUtc, report.publishTimeZone);
    return {
      state: "scheduled",
      badge: `Scheduled · ${when} (${report.publishTimeZone || "site time"})`,
      canHold: true,
      canSchedule: false,
      canPublishNow: true,
    };
  }
  if (report.scheduleState === "Stopped") {
    return {
      state: "stopped",
      badge: "Not published",
      reason: stopReasonText(report.scheduleError),
      reasonCode: report.scheduleError || "",
      canHold: false,
      canSchedule: Boolean(def),
      scheduleLabel: def ? `Schedule ${formatSiteTime(def.publishAtUtc, def.timeZone)}` : "",
      canPublishNow: true,
    };
  }
  if (!def) return null;
  return {
    state: "approved",
    badge: "",
    canHold: false,
    canSchedule: true,
    scheduleLabel: `Schedule ${formatSiteTime(def.publishAtUtc, def.timeZone)}`,
    canPublishNow: true,
  };
}
